import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import MetaTags from 'react-meta-tags';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import FeatherIcon from 'feather-icons-react';
import '../../assets/css/rentdigicare.css';
import {
  getAllMROSuppliers,
  createMROSupplier,
  updateMROSupplier,
  deleteMROSupplier,
  generateOTP,
  generatePasswordReset,
} from './utils/mroApi';

// MIUI-inspired styles
const miuiStyles = `
  .miui-card {
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    background: #fff;
    margin-bottom: 20px;
  }
  .miui-button {
    border-radius: 20px;
    padding: 8px 24px;
    font-weight: 500;
    text-transform: none;
    transition: all 0.3s ease;
  }
  .miui-dialog {
    .MuiDialog-paper {
      border-radius: 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      background: #fff;
      min-width: 600px;
    }
    .MuiDialogTitle-root {
      border-bottom: none;
      padding: 20px 24px;
      background: #f8f9fa;
      border-radius: 16px 16px 0 0;
    }
    .MuiDialogActions-root {
      border-top: none;
      padding: 16px 24px;
    }
    .MuiDialogContent-root {
      padding: 20px 24px;
    }
  }
  .miui-form-field {
    margin-bottom: 16px;
  }
  .miui-table {
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    .table {
      margin-bottom: 0;
    }
    .table thead th {
      background: #f8f9fa;
      border-bottom: none;
      padding: 16px;
      font-weight: 500;
      color: #333;
    }
    .table td {
      padding: 16px;
      vertical-align: middle;
    }
  }
  .scrollable-table {
    max-height: 600px;
    overflow-y: auto;
    position: relative;
  }
  .loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
  .error-summary {
    color: red;
    margin-bottom: 16px;
  }
`;

// Validation schema for Add Vendor (no Security Details, optional address, Canada-specific)
const addValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  mobile: Yup.object().shape({
    country_code: Yup.string()
      .matches(/^\+1$/, 'Country code must be +1 for Canada')
      .required('Country code is required'),
    number: Yup.string()
      .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
      .required('Mobile number is required'),
  }),
  address: Yup.object().shape({
    street: Yup.string(),
    city: Yup.string(),
    province: Yup.string(),
    postal_code: Yup.string().matches(
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
      'Postal code must be in A1A 1A1 format'
    ),
    country: Yup.string()
      .matches(/^Canada$/, 'Country must be Canada')
      .required('Country is required'),
  }),
  description: Yup.string(),
  is_active: Yup.boolean(),
});

// Validation schema for Edit Vendor (includes Security Details)
const editValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(8, 'Password must be at least 8 characters'),
  mobile: Yup.object().shape({
    country_code: Yup.string()
      .matches(/^\+1$/, 'Country code must be +1 for Canada')
      .required('Country code is required'),
    number: Yup.string()
      .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
      .required('Mobile number is required'),
  }),
  address: Yup.object().shape({
    street: Yup.string(),
    city: Yup.string(),
    province: Yup.string(),
    postal_code: Yup.string().matches(
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
      'Postal code must be in A1A 1A1 format'
    ),
    country: Yup.string()
      .matches(/^Canada$/, 'Country must be Canada')
      .required('Country is required'),
  }),
  description: Yup.string(),
  is_active: Yup.boolean(),
  otp: Yup.string(),
  otp_expiry: Yup.date().nullable(),
  password_reset_token: Yup.string(),
  password_reset_token_expiry: Yup.date().nullable(),
});

const CreateVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refs for input fields to focus on validation errors
  const inputRefs = {
    first_name: useRef(null),
    last_name: useRef(null),
    email: useRef(null),
    password: useRef(null),
    mobile_country_code: useRef(null),
    mobile_number: useRef(null),
    address_street: useRef(null),
    address_city: useRef(null),
    address_province: useRef(null),
    address_postal_code: useRef(null),
    address_country: useRef(null),
    description: useRef(null),
  };

  useEffect(() => {
    // Inject MIUI styles
    const styleSheet = document.createElement('style');
    styleSheet.textContent = miuiStyles;
    document.head.appendChild(styleSheet);
    fetchVendors();
    return () => document.head.removeChild(styleSheet);
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await getAllMROSuppliers();
      setVendors(response.data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      setLoading(true);
      try {
        await deleteMROSupplier(id);
        toast.success('Vendor deleted successfully');
        fetchVendors();
      } catch (error) {
        toast.error(error.message || 'Failed to delete vendor');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGenerateOTP = async (id, setFieldValue) => {
    setLoading(true);
    try {
      const response = await generateOTP(id);
      setFieldValue('otp', response.data.otp);
      setFieldValue('otp_expiry', new Date(response.data.otp_expiry).toISOString().slice(0, 16));
      toast.success('OTP generated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to generate OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePasswordReset = async (id, setFieldValue) => {
    setLoading(true);
    try {
      const response = await generatePasswordReset(id);
      setFieldValue('password_reset_token', response.data.password_reset_token);
      setFieldValue(
        'password_reset_token_expiry',
        new Date(response.data.password_reset_token_expiry).toISOString().slice(0, 16)
      );
      toast.success('Password reset token generated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to generate password reset token');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      dataField: 'reference_id',
      text: 'Reference ID',
      sort: true,
    },
    {
      dataField: 'first_name',
      text: 'First Name',
      sort: true,
    },
    {
      dataField: 'last_name',
      text: 'Last Name',
      sort: true,
    },
    {
      dataField: 'email',
      text: 'Email',
      sort: true,
    },
    {
      dataField: 'is_active',
      text: 'Active',
      formatter: (cell) => (cell ? 'Yes' : 'No'),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      formatter: (cell, row) => (
        <Box display="flex" gap={1}>
          <IconButton
            onClick={() => {
              setViewData(row);
              setViewModalOpen(true);
            }}
            title="View"
          >
            <FeatherIcon icon="eye" size={20} />
          </IconButton>
          <IconButton
            onClick={() => {
              setEditData(row);
              setEditModalOpen(true);
            }}
            title="Edit"
          >
            <FeatherIcon icon="edit" size={20} />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(row._id)}
            title="Delete"
          >
            <FeatherIcon icon="trash-2" size={20} color="red" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const defaultSorted = [{ dataField: 'first_name', order: 'asc' }];

  // Function to focus the first invalid field
  const focusFirstInvalidField = (errors = {}, touched = {}) => {
    const errorFields = [];

    // Check top-level fields
    ['first_name', 'last_name', 'email', 'password', 'description'].forEach((field) => {
      if (touched[field] && errors[field]) {
        errorFields.push({ field, error: errors[field] });
      }
    });

    // Check mobile fields
    if (touched.mobile && errors.mobile) {
      if (errors.mobile.country_code) {
        errorFields.push({ field: 'mobile_country_code', error: errors.mobile.country_code });
      }
      if (errors.mobile.number) {
        errorFields.push({ field: 'mobile_number', error: errors.mobile.number });
      }
    }

    // Check address fields
    if (touched.address && errors.address) {
      ['street', 'city', 'province', 'postal_code', 'country'].forEach((subField) => {
        if (errors.address[subField]) {
          errorFields.push({ field: `address_${subField}`, error: errors.address[subField] });
        }
      });
    }

    if (errorFields.length > 0) {
      const firstError = errorFields[0];
      toast.error(`Validation error: ${firstError.error}`);
      const ref = inputRefs[firstError.field];
      if (ref.current) {
        ref.current.focus();
      }
      return errorFields;
    }
    return [];
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Vendors | Rent Digi Care</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumb title="Home" breadcrumbItem="Vendors" />
          <div className="row">
            <div className="col-12">
              <Card className="miui-card">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="h4">Vendors</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      className="miui-button"
                      onClick={() => setModalOpen(true)}
                      disabled={loading}
                    >
                      Add Vendor
                    </Button>
                  </Box>
                  {loading ? (
                    <Box className="loader-container">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <div className="miui-table scrollable-table">
                      <BootstrapTable
                        keyField="_id"
                        data={vendors}
                        columns={columns}
                        defaultSorted={defaultSorted}
                        pagination={paginationFactory()}
                        bootstrap4
                        striped
                        hover
                        condensed
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vendor Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        className="miui-dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Vendor</DialogTitle>
        <Formik
          initialValues={{
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            mobile: { country_code: '+1', number: '' },
            address: { street: '', city: '', province: '', postal_code: '', country: 'Canada' },
            description: '',
            is_active: true,
          }}
          validationSchema={addValidationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={async (values, { setSubmitting, resetForm, validateForm }) => {
            const errors = await validateForm(values);
            const touched = {
              first_name: true,
              last_name: true,
              email: true,
              password: true,
              mobile: { country_code: true, number: true },
              address: { street: true, city: true, province: true, postal_code: true, country: true },
              description: true,
              is_active: true,
            };
            const errorFields = focusFirstInvalidField(errors, touched);
            if (errorFields.length > 0) {
              setSubmitting(false);
              return;
            }
            setLoading(true);
            try {
              await createMROSupplier(values);
              toast.success('Vendor created successfully');
              setModalOpen(false);
              resetForm();
              fetchVendors();
            } catch (error) {
              console.error('Create vendor error:', error);
              toast.error(error.message || 'Failed to create vendor');
            } finally {
              setLoading(false);
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, values, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Box className="miui-form-field">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="first_name"
                        label="First Name"
                        fullWidth
                        error={touched.first_name && !!errors.first_name}
                        helperText={touched.first_name && errors.first_name}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.first_name}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="last_name"
                        label="Last Name"
                        fullWidth
                        error={touched.last_name && !!errors.last_name}
                        helperText={touched.last_name && errors.last_name}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.last_name}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box className="miui-form-field">
                  <Field
                    as={TextField}
                    name="email"
                    label="Email"
                    type="email"
                    fullWidth
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    disabled={isSubmitting || loading}
                    inputRef={inputRefs.email}
                  />
                </Box>
                <Box className="miui-form-field">
                  <Field
                    as={TextField}
                    name="password"
                    label="Password"
                    type="password"
                    fullWidth
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    disabled={isSubmitting || loading}
                    inputRef={inputRefs.password}
                  />
                </Box>
                <Box className="miui-form-field">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Field
                        as={TextField}
                        name="mobile.country_code"
                        label="Country Code"
                        fullWidth
                        error={touched.mobile?.country_code && !!errors.mobile?.country_code}
                        helperText={touched.mobile?.country_code && errors.mobile?.country_code}
                        disabled={true}
                        inputRef={inputRefs.mobile_country_code}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Field
                        as={TextField}
                        name="mobile.number"
                        label="Mobile Number"
                        fullWidth
                        error={touched.mobile?.number && !!errors.mobile?.number}
                        helperText={touched.mobile?.number && errors.mobile?.number}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.mobile_number}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Address Details
                </Typography>
                <Box className="miui-form-field">
                  <Field
                    as={TextField}
                    name="address.street"
                    label="Street"
                    fullWidth
                    error={touched.address?.street && !!errors.address?.street}
                    helperText={touched.address?.street && errors.address?.street}
                    disabled={isSubmitting || loading}
                    inputRef={inputRefs.address_street}
                  />
                </Box>
                <Box className="miui-form-field">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="address.city"
                        label="City"
                        fullWidth
                        error={touched.address?.city && !!errors.address?.city}
                        helperText={touched.address?.city && errors.address?.city}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.address_city}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="address.province"
                        label="Province"
                        fullWidth
                        error={touched.address?.province && !!errors.address?.province}
                        helperText={touched.address?.province && errors.address?.province}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.address_province}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box className="miui-form-field">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="address.postal_code"
                        label="Postal Code (e.g., A1A 1A1)"
                        fullWidth
                        error={touched.address?.postal_code && !!errors.address?.postal_code}
                        helperText={touched.address?.postal_code && errors.address?.postal_code}
                        disabled={isSubmitting || loading}
                        inputRef={inputRefs.address_postal_code}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="address.country"
                        label="Country"
                        fullWidth
                        error={touched.address?.country && !!errors.address?.country}
                        helperText={touched.address?.country && errors.address?.country}
                        disabled={true}
                        inputRef={inputRefs.address_country}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box className="miui-form-field">
                  <Field
                    as={TextField}
                    name="description"
                    label="Description"
                    multiline
                    rows={4}
                    fullWidth
                    error={touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    disabled={isSubmitting || loading}
                    inputRef={inputRefs.description}
                  />
                </Box>
                <Box className="miui-form-field">
                  <FormControlLabel
                    control={
                      <Field
                        as={Checkbox}
                        name="is_active"
                        checked={values.is_active}
                        disabled={isSubmitting || loading}
                      />
                    }
                    label="Active"
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  className="miui-button"
                  onClick={() => setModalOpen(false)}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="miui-button"
                  disabled={isSubmitting || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Vendor'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* View Vendor Dialog */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        className="miui-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>View Vendor</DialogTitle>
        <DialogContent>
          {viewData && (
            <Box>
              <Typography><strong>Reference ID:</strong> {viewData.reference_id || 'N/A'}</Typography>
              <Typography><strong>First Name:</strong> {viewData.first_name || 'N/A'}</Typography>
              <Typography><strong>Last Name:</strong> {viewData.last_name || 'N/A'}</Typography>
              <Typography><strong>Email:</strong> {viewData.email || 'N/A'}</Typography>
              <Typography>
                <strong>Mobile:</strong>{' '}
                {viewData.mobile?.country_code && viewData.mobile?.number
                  ? `${viewData.mobile.country_code} ${viewData.mobile.number}`
                  : 'N/A'}
              </Typography>
              <Typography>
                <strong>Address:</strong>{' '}
                {viewData.address?.street
                  ? `${viewData.address.street}, ${viewData.address.city}, ${viewData.address.province}, ${viewData.address.postal_code}, ${viewData.address.country}`
                  : 'N/A'}
              </Typography>
              <Typography><strong>Description:</strong> {viewData.description || 'N/A'}</Typography>
              <Typography><strong>Active:</strong> {viewData.is_active ? 'Yes' : 'No'}</Typography>
              <Typography><strong>OTP:</strong> {viewData.otp || 'N/A'}</Typography>
              <Typography>
                <strong>OTP Expiry:</strong>{' '}
                {viewData.otp_expiry && !isNaN(new Date(viewData.otp_expiry))
                  ? new Date(viewData.otp_expiry).toLocaleString()
                  : 'N/A'}
              </Typography>
              <Typography><strong>Password Reset Token:</strong> {viewData.password_reset_token || 'N/A'}</Typography>
              <Typography>
                <strong>Password Reset Token Expiry:</strong>{' '}
                {viewData.password_reset_token_expiry &&
                !isNaN(new Date(viewData.password_reset_token_expiry))
                  ? new Date(viewData.password_reset_token_expiry).toLocaleString()
                  : 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            className="miui-button"
            onClick={() => setViewModalOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        className="miui-dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Vendor</DialogTitle>
        {editData && (
          <Formik
            initialValues={{
              first_name: editData.first_name || '',
              last_name: editData.last_name || '',
              email: editData.email || '',
              password: '',
              mobile: {
                country_code: editData.mobile?.country_code || '+1',
                number: editData.mobile?.number || '',
              },
              address: {
                street: editData.address?.street || '',
                city: editData.address?.city || '',
                province: editData.address?.province || '',
                postal_code: editData.address?.postal_code || '',
                country: editData.address?.country || 'Canada',
              },
              description: editData.description || '',
              is_active: editData.is_active || false,
              otp: editData.otp || '',
              otp_expiry:
                editData.otp_expiry && !isNaN(new Date(editData.otp_expiry))
                  ? new Date(editData.otp_expiry).toISOString().slice(0, 16)
                  : '',
              password_reset_token: editData.password_reset_token || '',
              password_reset_token_expiry:
                editData.password_reset_token_expiry &&
                !isNaN(new Date(editData.password_reset_token_expiry))
                  ? new Date(editData.password_reset_token_expiry).toISOString().slice(0, 16)
                  : '',
            }}
            validationSchema={editValidationSchema}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={async (values, { setSubmitting }) => {
              setLoading(true);
              try {
                await updateMROSupplier(editData._id, values);
                toast.success('Vendor updated successfully');
                setEditModalOpen(false);
                fetchVendors();
              } catch (error) {
                console.error('Update vendor error:', error);
                toast.error(error.message || 'Failed to update vendor');
              } finally {
                setLoading(false);
                setSubmitting(false);
              }
            }}
          >
            {({ errors, touched, values, isSubmitting, setFieldValue }) => (
              <Form>
                <DialogContent>
                  <Box className="miui-form-field">
                    <TextField
                      label="Reference ID"
                      value={editData.reference_id || 'N/A'}
                      fullWidth
                      disabled
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="first_name"
                          label="First Name"
                          fullWidth
                          error={touched.first_name && !!errors.first_name}
                          helperText={touched.first_name && errors.first_name}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="last_name"
                          label="Last Name"
                          fullWidth
                          error={touched.last_name && !!errors.last_name}
                          helperText={touched.last_name && errors.last_name}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="email"
                      label="Email"
                      type="email"
                      fullWidth
                      error={touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="password"
                      label="Password (leave blank to keep unchanged)"
                      type="password"
                      fullWidth
                      error={touched.password && !!errors.password}
                      helperText={touched.password && errors.password}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Field
                          as={TextField}
                          name="mobile.country_code"
                          label="Country Code"
                          fullWidth
                          error={touched.mobile?.country_code && !!errors.mobile?.country_code}
                          helperText={touched.mobile?.country_code && errors.mobile?.country_code}
                          disabled={true}
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <Field
                          as={TextField}
                          name="mobile.number"
                          label="Mobile Number"
                          fullWidth
                          error={touched.mobile?.number && !!errors.mobile?.number}
                          helperText={touched.mobile?.number && errors.mobile?.number}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Address Details
                  </Typography>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="address.street"
                      label="Street"
                      fullWidth
                      error={touched.address?.street && !!errors.address?.street}
                      helperText={touched.address?.street && errors.address?.street}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="address.city"
                          label="City"
                          fullWidth
                          error={touched.address?.city && !!errors.address?.city}
                          helperText={touched.address?.city && errors.address?.city}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="address.province"
                          label="Province"
                          fullWidth
                          error={touched.address?.province && !!errors.address?.province}
                          helperText={touched.address?.province && errors.address?.province}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className="miui-form-field">
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="address.postal_code"
                          label="Postal Code (e.g., A1A 1A1)"
                          fullWidth
                          error={touched.address?.postal_code && !!errors.address?.postal_code}
                          helperText={touched.address?.postal_code && errors.address?.postal_code}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="address.country"
                          label="Country"
                          fullWidth
                          error={touched.address?.country && !!errors.address?.country}
                          helperText={touched.address?.country && errors.address?.country}
                          disabled={true}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="description"
                      label="Description"
                      multiline
                      rows={4}
                      fullWidth
                      error={touched.description && !!errors.description}
                      helperText={touched.description && errors.description}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="is_active"
                          checked={values.is_active}
                          disabled={isSubmitting || loading}
                        />
                      }
                      label="Active"
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Security Details
                  </Typography>
                  <Box className="miui-form-field">
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <Field
                          as={TextField}
                          name="otp"
                          label="OTP"
                          fullWidth
                          error={touched.otp && !!errors.otp}
                          helperText={touched.otp && errors.otp}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant="outlined"
                          className="miui-button"
                          onClick={() => handleGenerateOTP(editData._id, setFieldValue)}
                          disabled={isSubmitting || loading}
                          fullWidth
                        >
                          Generate OTP
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="otp_expiry"
                      label="OTP Expiry"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={touched.otp_expiry && !!errors.otp_expiry}
                      helperText={touched.otp_expiry && errors.otp_expiry}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                  <Box className="miui-form-field">
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <Field
                          as={TextField}
                          name="password_reset_token"
                          label="Password Reset Token"
                          fullWidth
                          error={touched.password_reset_token && !!errors.password_reset_token}
                          helperText={touched.password_reset_token && errors.password_reset_token}
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant="outlined"
                          className="miui-button"
                          onClick={() => handleGeneratePasswordReset(editData._id, setFieldValue)}
                          disabled={isSubmitting || loading}
                          fullWidth
                        >
                          Generate Token
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className="miui-form-field">
                    <Field
                      as={TextField}
                      name="password_reset_token_expiry"
                      label="Password Reset Token Expiry"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={touched.password_reset_token_expiry && !!errors.password_reset_token_expiry}
                      helperText={touched.password_reset_token_expiry && errors.password_reset_token_expiry}
                      disabled={isSubmitting || loading}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="outlined"
                    className="miui-button"
                    onClick={() => setEditModalOpen(false)}
                    disabled={isSubmitting || loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="miui-button"
                    disabled={isSubmitting || loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Update Vendor'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>

      <ToastContainer />
    </React.Fragment>
  );
};

export default CreateVendors;